import React, { useState } from 'react';
import styled from 'styled-components';
import { Calendar, Clock, CalendarDays, Plus } from 'lucide-react';
import {
  Reminder
} from '../../services/reminderService';
import ReminderCard from './ReminderCard';
import CreateReminderModal from './CreateReminderModal';

const WidgetContainer = styled.div<{ $embedded?: boolean }>`
  background: ${props => props.$embedded ? 'transparent' : 'white'};
  border-radius: ${props => props.$embedded ? '0' : '16px'};
  box-shadow: ${props => props.$embedded ? 'none' : '0 1px 4px rgba(0, 0, 0, 0.04)'};
  padding: ${props => props.$embedded ? '0' : '28px'};
  margin-bottom: ${props => props.$embedded ? '0' : '28px'};
`;

const WidgetHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const WidgetTitleContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const WidgetIcon = styled.div`
  display: flex;
  align-items: center;
  color: var(--primary-color);
  filter: saturate(0.7) brightness(0.9);
`;

const WidgetTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
`;

const Badge = styled.span<{ $variant?: 'error' | 'warning' | 'info' }>`
  font-size: 13px;
  font-weight: 600;
  color: ${props => {
    switch (props.$variant) {
      case 'error': return '#dc2626';
      case 'warning': return '#d97706';
      case 'info': return 'var(--primary-color)';
      default: return 'var(--text-secondary)';
    }
  }};
`;

const TabContainer = styled.div`
  display: flex;
  gap: 10px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid var(--border-color);
`;

const Tab = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: transparent;
  border: none;
  border-bottom: 2px solid ${props => props.$active ? 'var(--primary-color)' : 'transparent'};
  border-radius: 0;
  font-size: 14px;
  font-weight: ${props => props.$active ? '600' : '500'};
  color: ${props => props.$active ? 'var(--primary-color)' : 'var(--text-secondary)'};
  cursor: pointer;
  transition: all 0.2s ease;
  margin-bottom: -17px;

  &:hover {
    color: var(--primary-color);
  }
`;

const RemindersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  max-height: 350px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--border-color);
    border-radius: 2px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: var(--text-tertiary);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: var(--text-secondary);
`;

const EmptyIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  color: var(--text-secondary);
`;

const EmptyText = styled.p`
  font-size: 14px;
  margin: 0;
`;

const CreateButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--primary-color);
  color: white;
  border: 1px solid var(--primary-color);
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: transparent;
    color: var(--primary-color);
  }
`;

const ViewAllButton = styled.button`
  background: none;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  padding: 12px 16px;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-primary);
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: 16px;
  width: 100%;

  &:hover {
    border-color: var(--primary-color);
    color: var(--primary-color);
  }
`;

interface RemindersWidgetProps {
  reminders: {
    today: Reminder[];
    overdue: Reminder[];
    upcoming: Reminder[];
  };
  counts: {
    todayCount: number;
    overdueCount: number;
    upcomingCount: number;
  };
  onReminderUpdate?: () => void;
  /** Hide the widget header when embedded inside a tab */
  embedded?: boolean;
}

const RemindersWidget: React.FC<RemindersWidgetProps> = ({
  reminders,
  counts,
  onReminderUpdate,
  embedded = false
}) => {
  const [activeTab, setActiveTab] = useState<'today' | 'overdue' | 'upcoming'>('today');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const getActiveReminders = () => {
    switch (activeTab) {
      case 'today': return reminders.today;
      case 'overdue': return reminders.overdue;
      case 'upcoming': return reminders.upcoming;
      default: return [];
    }
  };

  const handleReminderAction = () => {
    if (onReminderUpdate) {
      onReminderUpdate();
    }
  };

  const activeReminders = getActiveReminders();

  return (
    <>
      <WidgetContainer $embedded={embedded}>
        {!embedded && (
          <WidgetHeader>
            <WidgetTitleContainer>
              <WidgetIcon>
                <Calendar size={20} />
              </WidgetIcon>
              <WidgetTitle>התזכורות שלי</WidgetTitle>
            </WidgetTitleContainer>
            <CreateButton onClick={() => setShowCreateModal(true)}>
              <Plus size={16} />
              תזכורת חדשה
            </CreateButton>
          </WidgetHeader>
        )}

        {embedded && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
            <CreateButton onClick={() => setShowCreateModal(true)}>
              <Plus size={16} />
              תזכורת חדשה
            </CreateButton>
          </div>
        )}

        <TabContainer>
          <Tab
            $active={activeTab === 'today'}
            onClick={() => setActiveTab('today')}
          >
            היום
            {counts.todayCount > 0 && (
              <Badge $variant="info">
                {counts.todayCount}
              </Badge>
            )}
          </Tab>
          <Tab
            $active={activeTab === 'overdue'}
            onClick={() => setActiveTab('overdue')}
          >
            פג תוקף
            {counts.overdueCount > 0 && (
              <Badge $variant="error">
                {counts.overdueCount}
              </Badge>
            )}
          </Tab>
          <Tab
            $active={activeTab === 'upcoming'}
            onClick={() => setActiveTab('upcoming')}
          >
            קרובות
            {counts.upcomingCount > 0 && (
              <Badge $variant="warning">
                {counts.upcomingCount}
              </Badge>
            )}
          </Tab>
        </TabContainer>

        {activeReminders.length > 0 ? (
          <>
            <RemindersList>
              {activeReminders.map(reminder => (
                <ReminderCard
                  key={reminder.id}
                  reminder={reminder}
                  onUpdate={handleReminderAction}
                />
              ))}
            </RemindersList>
            {activeReminders.length > 3 && (
              <ViewAllButton onClick={() => window.location.href = '/reminders'}>
                הצג את כל התזכורות
              </ViewAllButton>
            )}
          </>
        ) : (
          <EmptyState>
            <EmptyIcon>
              {activeTab === 'today' && <Calendar size={28} />}
              {activeTab === 'overdue' && <Clock size={28} />}
              {activeTab === 'upcoming' && <CalendarDays size={28} />}
            </EmptyIcon>
            <EmptyText>
              {activeTab === 'today' && 'אין תזכורות להיום'}
              {activeTab === 'overdue' && 'אין תזכורות שפג תוקפן'}
              {activeTab === 'upcoming' && 'אין תזכורות קרובות'}
            </EmptyText>
          </EmptyState>
        )}
      </WidgetContainer>

      {showCreateModal && (
        <CreateReminderModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSave={() => {
            setShowCreateModal(false);
            handleReminderAction();
          }}
        />
      )}
    </>
  );
};

export default RemindersWidget;
